import { IEmployee, EmployeesService, EnumService, IEmployeeSalaryScale } from "../../../services/index";
import { ISalaryScaleGroup, SalaryScaleGroupService, ISalaryScale, SalaryScaleService } from "../../../services/salaryScaleGroup.service";


interface IResolve {
    employee: IEmployee;
    functions: any;
    transportationTypes: any;
    salaryScales: any;
    users: any;
}

class EmployeesItemBasicController {
    private resolve: IResolve;
    private employee: IEmployee;
    private functions: any;
    private transportationTypes: any;
    private salaryScales: any;
    private employeeSalaryScales: IEmployeeSalaryScale[] = [];
    private salaryScaleGroups: ISalaryScaleGroup[] = [];
    private availableScalesByGroup: { [groupId: string]: ISalaryScale[] } = {};
    private errorMessages: { [scaleNumber: number]: string } = {};

    constructor(
        private EnumService: EnumService,
        private EmployeesService: EmployeesService,
        private SalaryScaleGroupService: SalaryScaleGroupService,
        private SalaryScaleService: SalaryScaleService,
        private $state: ng.ui.IStateService
    ) {
        'ngInject';
    }

    public $onInit = () => {    
        this.employee = this.resolve.employee;
        this.functions = this.resolve.functions;
        this.transportationTypes = this.resolve.transportationTypes;
        this.salaryScales = this.resolve.salaryScales;
        
        // Initialize altijd 3 lege schalen
        this.initializeSalaryScales();
        
        if (this.employee && this.employee.id) {
            this.loadEmployeeSalaryScales();
        }
        
        this.loadSalaryScaleGroups();
    }

    private initializeSalaryScales = () => {
        // Maak altijd 3 schalen aan (ook als leeg)
        this.employeeSalaryScales = [];
        for (let i = 1; i <= 3; i++) {
            this.employeeSalaryScales.push({
                id: null,
                employeeId: (this.employee && this.employee.id) || null,
                salaryScaleId: null,
                order: i - 1,
                scaleNumber: i,
                salaryScale: null,
                newSalaryScaleGroupId: null,
                newSalaryScaleId: null
            } as any);
        }
    }

    private loadEmployeeSalaryScales = () => {
        this.EmployeesService.getSalaryScales(this.employee.id).then((scales: IEmployeeSalaryScale[]) => {
            // Zorg dat er altijd 3 schalen zijn (1, 2, 3)
            const scalesMap: { [scaleNumber: number]: IEmployeeSalaryScale } = {};
            
            if (scales && scales.length > 0) {
                scales.forEach(scale => {
                    if (scale.scaleNumber) {
                        scalesMap[scale.scaleNumber] = scale;
                    }
                });
            }
            
            // Vul de array met bestaande schalen of lege schalen
            for (let i = 1; i <= 3; i++) {
                if (scalesMap[i]) {
                    this.employeeSalaryScales[i - 1] = {
                        ...scalesMap[i],
                        newSalaryScaleGroupId: null,
                        newSalaryScaleId: null
                    } as any;
                } else {
                    // Zorg dat scaleNumber altijd is ingesteld
                    this.employeeSalaryScales[i - 1] = {
                        id: null,
                        employeeId: this.employee.id,
                        salaryScaleId: null,
                        order: i - 1,
                        scaleNumber: i,
                        salaryScale: null,
                        newSalaryScaleGroupId: null,
                        newSalaryScaleId: null
                    } as any;
                }
            }
        });
    }

    private loadSalaryScaleGroups = () => {
        this.SalaryScaleGroupService.all().then((groups: ISalaryScaleGroup[]) => {
            this.salaryScaleGroups = groups.sort((a, b) => {
                if (a.name < b.name) return -1;
                if (a.name > b.name) return 1;
                return 0;
            });
            
            // Load scales for each group
            this.salaryScaleGroups.forEach(group => {
                if (group.id) {
                    this.SalaryScaleService.getBySalaryScaleGroupId(group.id).then((scales: ISalaryScale[]) => {
                        this.availableScalesByGroup[group.id] = scales;
                    });
                }
            });
        });
    }

    public onGroupChange = (groupId: string, scaleNumber: number) => {
        const scale = this.employeeSalaryScales[scaleNumber - 1];
        (scale as any).newSalaryScaleId = null;
        
        if (groupId && !this.availableScalesByGroup[groupId]) {
            this.SalaryScaleService.getBySalaryScaleGroupId(groupId).then((scales: ISalaryScale[]) => {
                this.availableScalesByGroup[groupId] = scales;
            });
        }
    }

    public getSalaryScaleGroupName = (groupId: string): string => {
        if (!groupId || !this.salaryScaleGroups || this.salaryScaleGroups.length === 0) {
            return '';
        }
        const group = this.salaryScaleGroups.find(g => g.id === groupId);
        return group ? group.name : '';
    }

    public getAvailableGroupsForScale = (scaleNumber: number): ISalaryScaleGroup[] => {
        if (!this.salaryScaleGroups || this.salaryScaleGroups.length === 0) {
            return [];
        }
        
        // Haal de ingevulde CAO's op van andere schalen
        const usedGroupIds = this.employeeSalaryScales
            .filter((es, index) => index !== (scaleNumber - 1)) // Exclude huidige schaal
            .filter(es => es.salaryScale && es.salaryScale.salaryScaleGroupId)
            .map(es => es.salaryScale.salaryScaleGroupId);
        
        return this.salaryScaleGroups.filter(group => usedGroupIds.indexOf(group.id) === -1);
    }

    public addSalaryScale = (scaleNumber: number) => {
        this.errorMessages[scaleNumber] = null;
        
        const scale = this.employeeSalaryScales[scaleNumber - 1] as any;
        
        if (!scale.newSalaryScaleGroupId || !scale.newSalaryScaleId) {
            this.errorMessages[scaleNumber] = 'Selecteer zowel een CAO als een loonschaal';
            return;
        }

        // Check if group already exists in other scales
        const existingGroupIds = this.employeeSalaryScales
            .filter((es, index) => index !== (scaleNumber - 1))
            .filter(es => es.salaryScale && es.salaryScale.salaryScaleGroupId)
            .map(es => es.salaryScale.salaryScaleGroupId);
        
        if (existingGroupIds.indexOf(scale.newSalaryScaleGroupId) !== -1) {
            this.errorMessages[scaleNumber] = 'Deze CAO is al toegevoegd aan een andere schaal. Elke CAO kan slechts één keer worden toegevoegd.';
            return;
        }

        this.EmployeesService.addSalaryScaleToScaleNumber(
            this.employee.id, 
            scale.newSalaryScaleId, 
            scaleNumber
        ).then(() => {
            this.loadEmployeeSalaryScales();
            scale.newSalaryScaleGroupId = null;
            scale.newSalaryScaleId = null;
            this.errorMessages[scaleNumber] = null;
        }).catch((error) => {
            this.errorMessages[scaleNumber] = error.data || error.message || 'Fout bij toevoegen salary scale';
        });
    }

    public removeSalaryScale = (scaleNumber: number) => {
        this.EmployeesService.removeSalaryScaleFromScaleNumber(this.employee.id, scaleNumber).then(() => {
            this.loadEmployeeSalaryScales();
        }).catch((error) => {
            this.errorMessages[scaleNumber] = error.data || error.message || 'Fout bij verwijderen salary scale';
        });
    }

    public save = () => {
        this.employee.userName = this.employee.emailPrivate;
        if (this.employee.id != undefined) {
            this.EmployeesService.put(this.employee.id, this.employee).then((employee: IEmployee) => {
                 this.closeModal();
            });
        } else {
            this.EmployeesService.post(this.employee).then((employee: IEmployee) => {
                this.$state.go('^', { 'id': employee.id });
            });
        }
       
    }

    private closeModal() {
        this.$state.go('^', {}, { reload: true });
    }
}

export const employeesItemBasicComponent = { 
    bindings: {
        resolve : "<",
        close: "&"
    },
    controller: EmployeesItemBasicController,
    template: require('./employees.item.basic.html')
};



// WEBPACK FOOTER //
// ./src/app/modules/hr/employees/employees.item.basic.component.ts